import * as Sentry from '@sentry/nextjs'

export const UTM_SESSION_KEY = 'utm_params'

export const UTM_QUERY_FIELDS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_id',
  'utm_content',
  'utm_channel',
  'utm_adgroup',
  'adgroupid',
  'ad',
] as const

export type UtmParams = Partial<Record<(typeof UTM_QUERY_FIELDS)[number], string>>

type QueryLike = Record<string, string | string[] | undefined>

const toQueryRecord = (query: QueryLike | string): QueryLike => {
  if (typeof query !== 'string') {
    return query
  }

  const params = new URLSearchParams(
    query.startsWith('?') ? query.slice(1) : query,
  )
  const result: QueryLike = {}

  params.forEach((value, key) => {
    result[key] = value
  })

  return result
}

const toStringValue = (value: string | string[] | undefined): string | undefined => {
  if (Array.isArray(value)) {
    return value[0]
  }

  return value
}

export function parseUtmFromQuery(
  query: QueryLike | string,
): UtmParams | null {
  const normalizedQuery = toQueryRecord(query)

  if (!('utm_source' in normalizedQuery)) {
    return null
  }

  const utmSource = toStringValue(normalizedQuery.utm_source)
  if (!utmSource) {
    return null
  }

  const utmParams: UtmParams = { utm_source: utmSource }

  for (const field of UTM_QUERY_FIELDS) {
    if (field === 'utm_source') continue
    if (!(field in normalizedQuery)) continue

    const value = toStringValue(normalizedQuery[field])
    if (value) {
      utmParams[field] = value
    }
  }

  return utmParams
}

export function persistSessionUtmParams(
  query: QueryLike | string,
): UtmParams | null {
  if (typeof window === 'undefined') return null

  const utmParams = parseUtmFromQuery(query)
  if (!utmParams) return null

  try {
    sessionStorage.setItem(UTM_SESSION_KEY, JSON.stringify(utmParams))
  } catch (error) {
    Sentry.captureException(error)
    return null
  }

  return utmParams
}

/** Session-scoped; intentionally not cleared on logout for browser-session attribution. */
export function getSessionUtmParams(): UtmParams | null {
  if (typeof window === 'undefined') return null

  try {
    const stored = sessionStorage.getItem(UTM_SESSION_KEY)
    if (stored) {
      return JSON.parse(stored) as UtmParams
    }
  } catch (error) {
    Sentry.captureException(error)
  }

  return parseUtmFromQuery(window.location?.search ?? '')
}

export function mergeSessionUtmParams<T extends Record<string, unknown>>(
  params: T,
): T & UtmParams {
  const utmParams = getSessionUtmParams()
  if (!utmParams) {
    return params as T & UtmParams
  }

  return {
    ...utmParams,
    ...params,
  }
}

/** Merge UTMs into both event and extra payloads for multi-destination fan-out (e.g. fbq). */
export function withSessionUtmFanOut<
  T extends Record<string, unknown>,
  E extends Record<string, unknown>,
>(eventParams: T, extraParams: E = {} as E) {
  const utmParams = getSessionUtmParams()
  const mergedEventParams = mergeSessionUtmParams(eventParams)
  const mergedExtraParams = utmParams
    ? ({ ...utmParams, ...extraParams } as E & UtmParams)
    : extraParams

  return { mergedEventParams, mergedExtraParams }
}

export function isShareChatUtmSession(): boolean {
  return getSessionUtmParams()?.utm_source === 'sharechat'
}

/**
 * Pushes session UTMs for GTM. Requires analytics-team wiring of Data Layer
 * Variables / triggers on `session_utm_context` (see FSS-7793 test plan).
 * Called from _app on SPA navigations only to avoid duplicate pushes on landing.
 */
export function syncSessionUtmToDataLayer(pageLocation?: string): void {
  if (typeof window === 'undefined') return

  const utmParams = getSessionUtmParams()
  if (!utmParams) return

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'session_utm_context',
    page_location: pageLocation ?? window.location?.href ?? '',
    ...utmParams,
  })
}
